import { useState, useMemo, useCallback, memo } from 'react';
import { Flex, Drawer, Button, Pagination } from 'antd';
import { FREE_ASSETS, CATEGORIES } from './items/free-assets';
import { ProductCard } from '@ozoneuniverse/ui-components/build-pkg/src/components/asset-store/products/ProductCard';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import ModelViewerModal from './ModelViewer';
import { useBranding } from '@src/theme/branding/useBranding';
import styles from './web2-assets-store.module.css';

const FORMATS_TYPES = {
  MODEL: 2,
  IMAGE: 8,
  AUDIO: 6,
  VIDEO: 7,
};

const PAGE_LIMIT = 18;

function paginate(array: any[], currentPage: number): any[] {
  return array.slice(currentPage * PAGE_LIMIT, (currentPage + 1) * PAGE_LIMIT);
}

const Cards = ({ freeAssets, onOpenPreview, onLoadAsset }) => {
  return (
    <Flex gap={20} wrap='wrap'>
      {freeAssets?.map((el, i) => {
        const assetType = el.modelType || el.fileType;
        return (
          <ProductCard
            product={el}
            disableLink={true}
            disableButtons={true}
            disablePreview={assetType === 'SCENE'}
            type='web2'
            imgOnClick={
              assetType === 'SCENE'
                ? undefined
                : () => onOpenPreview(el.assetURL)
            }
            customButton={{
              text: 'Add to the scene',
              onClick: () =>
                onLoadAsset(el.assetURL, el.name, assetType, el.previewImage),
            }}
            key={`${i}-${el.name}`}
          />
        );
      })}
    </Flex>
  );
};

const CardsMemo = memo(Cards);

export default function FreeAssets({
  isOpen,
  setIsOpen,
  loadAsset,
  loadScene,
}) {
  const { isZepeto, isXsolla } = useBranding();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterType, setFilterType] = useState<string>('all');
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [modelUrl, setModelUrl] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [active, setActive] = useState<number>(0);
  const [currentLength, setCurrentLength] = useState<number>(0);
  const [activeCategory, setActiveCategory] = useState<number>(0);

  const freeAssets = useMemo(() => {
    const assets =
      filterType === 'all'
        ? FREE_ASSETS.single_asset
        : FREE_ASSETS[filterType] || [];

    if (filterCategory === 'all') {
      setCurrentLength(assets.length);
      return paginate(assets, currentPage);
    }
    const filteredAssets = assets.filter(
      (el) => el.categoryName === filterCategory
    );
    setCurrentLength(filteredAssets.length);
    return paginate(filteredAssets, currentPage);
  }, [filterType, currentPage, filterCategory]);

  const onOpenPreview = useCallback((url) => {
    setPreviewOpen(true);
    setModelUrl(url);
  }, []);

  const onLoadAsset = useCallback(
    (url, name, modelType, sceneThumbnail) => {
      if (modelType === 'SCENE') {
        loadScene(name, url, SIDE_MENU_EVENTS.SCENES, sceneThumbnail);
      } else {
        loadAsset(name, url, FORMATS_TYPES[modelType]);
      }

      setIsOpen(false);
    },
    [FORMATS_TYPES, SIDE_MENU_EVENTS, loadScene]
  );

  const resetFilters = useCallback(() => {
    setFilterCategory('all');
    setActiveCategory(0);
    setCurrentPage(0);
  }, []);

  const handleClickAll = useCallback(() => {
    setActive(0);
    setFilterType('all');
    resetFilters();
  }, []);

  const handleClickSingleAsset = useCallback(() => {
    setActive(1);
    setFilterType('single_asset');
    resetFilters();
  }, []);

  const handleClickZepeto = useCallback(() => {
    setActive(2);
    setFilterType('zepeto');
    resetFilters();
  }, []);

  const handleCategoryClick = useCallback((categoryName, index) => {
    setFilterCategory(categoryName);
    setActiveCategory(index + 1);
    setCurrentPage(0);
  }, []);

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        closeIcon={false}
        footer={false}
        width={'90%'}
        destroyOnClose
        zIndex={1001}
        className={styles.assetsDrawer}
        title={
          <div className={styles.topSection}>
            <h3 className={styles.title}>Free Assets</h3>
            <Flex align='center' justify='space-between'>
              <Flex align='flex-start' gap={25}>
                <Button
                  data-cy='free-assets-all'
                  onClick={handleClickAll}
                  className={`${active === 0 && styles.active} ${
                    styles.button
                  }`}
                >
                  <span className={styles.buttonText}>All</span>
                </Button>
                <Button
                  data-cy='free-assets-single-assets'
                  onClick={handleClickSingleAsset}
                  className={`${active === 1 && styles.active} ${
                    styles.button
                  }`}
                >
                  <span className={styles.buttonText}>Single assets</span>
                </Button>
                {isZepeto && (
                  <Button
                    data-cy='free-assets-zepeto'
                    onClick={handleClickZepeto}
                    className={`${active === 2 && styles.active} ${
                      styles.button
                    }`}
                  >
                    <span className={styles.buttonText}>Zepeto assets</span>
                  </Button>
                )}
              </Flex>
            </Flex>
            <Flex align='center' gap={5} style={{ marginTop: 10 }}>
              <Button
                onClick={resetFilters}
                className={`${activeCategory === 0 && styles.categoryActive} ${
                  styles.categoryButton
                }`}
              >
                <span className={styles.buttonText}>All</span>
              </Button>
              {filterType !== 'zepeto' &&
                CATEGORIES.map((categoryName, index) => (
                  <Button
                    key={index}
                    onClick={() => handleCategoryClick(categoryName, index)}
                    className={`${
                      activeCategory === index + 1 && styles.categoryActive
                    } ${styles.categoryButton}`}
                  >
                    <span className={styles.buttonText}>{categoryName}</span>
                  </Button>
                ))}
            </Flex>
          </div>
        }
      >
        <CardsMemo
          freeAssets={freeAssets}
          onOpenPreview={onOpenPreview}
          onLoadAsset={onLoadAsset}
        />
        <Flex justify='center'>
          <Pagination
            defaultCurrent={currentPage + 1}
            pageSize={PAGE_LIMIT}
            total={currentLength}
            hideOnSinglePage
            showSizeChanger={false}
            onChange={(page) => {
              setCurrentPage(page - 1);
            }}
            className={styles.pagination}
          />
        </Flex>
        <ModelViewerModal
          open={previewOpen}
          setOpen={setPreviewOpen}
          modelUrl={modelUrl}
        />
      </Drawer>
    </>
  );
}
